"use client";

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { BarChart2, MessagesSquare, Megaphone, ShoppingCart, ArrowRight, Settings, User, LayoutGrid, Briefcase, Loader2 } from 'lucide-react';
import { PageContainer, PageHeader, CardGrid, SurfaceCard } from '@/components/custom_ui';
import { useSession } from 'next-auth/react';

// Map agent types to their UI configuration
const agentConfig: Record<string, Omit<Agent, 'id' | 'status'>> = {
  'analytics': {
    title: 'Analytics Agent',
    description: 'Transforma datos en estrategia con objetivos, KPIs y análisis avanzados.',
    icon: BarChart2,
    url: '/analytics/dashboards',
    accentColor: '#06b6d4',
  },
  'customer_support': {
    title: 'Customer Support Agent',
    description: 'Gestiona y automatiza las interacciones con tus clientes.',
    icon: MessagesSquare,
    url: '/customer_support/ticketing',
    accentColor: '#f97316',
  },
  'communication': {
    title: 'Marketing & Communication Agent',
    description: 'Crea contenido para redes sociales con IA. Posts, blogs y campañas.',
    icon: Megaphone,
    url: '/communication/create',
    accentColor: '#d946ef',
  },
  'crm': {
    title: 'Commercial Agent',
    description: 'Potencia tus ventas y gestiona tu pipeline comercial.',
    icon: ShoppingCart,
    url: '/crm/contacts',
    accentColor: '#10b981',
  },
  'personal': {
    title: 'Personal Agent',
    description: 'Gestiona inbox, calendarios y recordatorios personales de forma inteligente.',
    icon: User,
    url: '/personals',
    accentColor: '#14b8a6',
  },
  'scheduler': {
    title: 'Scheduler Agent',
    description: 'Automatiza tareas programadas y recordatorios.',
    icon: LayoutGrid,
    url: '/scheduler',
    accentColor: '#6366f1',
  },
};

const settingsAgent: Agent = {
  title: 'User Settings',
  description: 'Gestiona tenants, usuarios y configuración del sistema.',
  icon: Settings,
  url: '/settings',
  status: 'active' as const,
  accentColor: '#64748b',
};

interface Agent {
  title: string;
  description: string;
  icon: any;
  url: string | null;
  status: 'active' | 'coming_soon';
  accentColor: string;
}

interface AgentCardProps {
  agent: Agent;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
  const CardContent = (
    <SurfaceCard 
      className={`relative flex flex-col h-full p-4 sm:p-6 transition-all duration-300 ${
        agent.status === 'active' 
          ? 'hover:shadow-xl hover:-translate-y-1 cursor-pointer' 
          : 'opacity-60 cursor-not-allowed'
      }`}
    >
      {agent.status === 'coming_soon' && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-gray-200 text-gray-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
          Próximamente
        </div>
      )}
      
      <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mb-3 sm:mb-4">
        <agent.icon className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: agent.accentColor }} />
      </div>
      
      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">{agent.title}</h3>
      <p className="text-xs sm:text-sm text-gray-600 flex-grow mb-3 sm:mb-4">{agent.description}</p>
      
      {agent.status === 'active' && (
        <div className="flex items-center font-semibold text-xs sm:text-sm group" style={{ color: agent.accentColor }}>
          <span>Acceder</span>
          <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      )}
    </SurfaceCard>
  );

  return agent.url ? <Link href={agent.url}>{CardContent}</Link> : <div>{CardContent}</div>;
};

export default function Dashboard() {
  const { data: session, status } = useSession();
  const role = session?.role || null;
  const isSuperAdmin = role === 'SuperAdmin';
  
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(status === 'loading' || !isSuperAdmin);

  useEffect(() => {
    // Wait for session to load
    if (status === 'loading') {
      return;
    }

    // SuperAdmin only sees Settings, no need to fetch agents
    if (isSuperAdmin) {
      setAgents([settingsAgent]);
      setLoading(false);
      return;
    }

    // For regular users, fetch their assigned agents
    // API route now injects tenant_id from session
    async function fetchMyAgents() {
      try {
        const response = await fetch(`/api/smh/my-agents`);
        const data = await response.json();
        
        // Map API agents to UI agents
        const uiAgents: Agent[] = data.agents
          .map((apiAgent: any) => {
            const config = agentConfig[apiAgent.type];
            if (!config) return null;
            return {
              ...config,
              status: 'active' as const,
            };
          })
          .filter(Boolean);
        
        setAgents(uiAgents);
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAgents([]);
      } finally {
        setLoading(false);
      }
    }

    fetchMyAgents();
  }, [isSuperAdmin, status]);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        icon={<img src="/White logo - no background - solo rueda (2).png" alt="SmartHub" className="w-10 h-10" />}
        heading={isSuperAdmin ? "SmartHub - Panel de Administración" : "SmartHub - Tu Hub de Agentes"}
        description={isSuperAdmin 
          ? "Gestiona tenants, usuarios y configuración del sistema." 
          : agents.length > 0 
            ? "Selecciona un agente para empezar a potenciar tu negocio."
            : "No tienes agentes asignados. Contacta al administrador para obtener acceso."
        }
      />
        
      {agents.length > 0 ? (
        <CardGrid variant="cards" gap="lg">
          {agents.map((agent) => (
            <AgentCard key={agent.title} agent={agent} />
          ))}
        </CardGrid>
      ) : !isSuperAdmin && (
        <SurfaceCard className="p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <LayoutGrid className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Sin agentes asignados</h3>
          <p className="text-gray-600">
            Tu cuenta no tiene agentes asignados todavía. 
            Contacta al administrador del sistema para obtener acceso.
          </p>
        </SurfaceCard>
      )}
    </PageContainer>
  );
}
